// Import React Libraries
import React, { useState } from "react";

import { IonButton, IonLoading, useIonToast } from "@ionic/react";

import { useHistory, useLocation } from "react-router";
import { PasswordInput } from "../../../components/PasswordInput/PasswordInput";

// Import Css File
import styles from "./ResetPassword.module.scss";
import classNames from "classnames/bind";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import CommonFormElement from "../../../components/UI/CommonFormElements/CommonFormElement";
import commonFormStyles from "../../../components/UI/CommonFormElements/CommonFormElements.module.scss";
import { ValidatePasswordInput } from "../../../components/ValidatePasswordInput/ValidatePasswordInput";
import { useResetPasswordMutation } from "../../../providers/Auth";
import useWindowSize from "../../../utils/useWindowSize";
import Page from "../../page-layout";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);
const commonForm = classNames.bind(commonFormStyles);

const ResetPassword = () => {
  const { t } = useTranslation();
  const { mutate: resetPasswordMutation } = useResetPasswordMutation();
  const [repeatPassword, setRepeatPassword] = useState("");
  const [password, setPassword] = useState("");
  const [showLoading, setShowLoading] = useState(false); // For Loading
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const location = useLocation<{
    verificationCodeData: {
      code: string;
      code_id: number;
      phone_number: string;
    };
  }>();
  const history = useHistory();

  const [presentToast] = useIonToast();

  const showErrorToast = () => {
    presentToast({
      message: t("An unexpected error occurred"),
      color: "danger",
      duration: 2000,
    });
  };

  const resetPassword = () => {
    setShowLoading(true);
    const verificationCodeData = location.state.verificationCodeData;

    resetPasswordMutation(
      {
        phone: verificationCodeData.phone_number,
        newPassword: password,
        verificationCode: verificationCodeData.code,
        verificationCodeId: verificationCodeData.code_id.toString(),
      },
      {
        onError: () => {
          showErrorToast();
          setShowLoading(false);
        },
        onSuccess: () => {
          setShowLoading(false);
          history.goBack();
        },
      },
    );
  };
  const { width } = useWindowSize();

  return (
    <Page>
      <Page.HeaderMobile showBackButton />

      <Page.Content className="about-us">
        <Page.HeaderDesktop />
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={t("Please wait...")}
          duration={20000}
        />
        <div className={`${cx("resetPassword__content")} global-content`}>
          <MaxWidthContainer maxWidthBreakpoint={"Sm"} classList={cx("resetPassword__container")}>
            <form
              onSubmit={(e) => {
                resetPassword();
                e.preventDefault();
              }}
            >
              <CommonFormElement
                label={t("New Password")}
                errorMessage={t(passwordError)}
                isValid={!!(password.length && isPasswordValid)}
              >
                <ValidatePasswordInput
                  name="c_d"
                  required={true}
                  value={password}
                  setPassword={(value) => setPassword(value)}
                  setPasswordError={(error) => setPasswordError(error)}
                  setIsPasswordValid={(isValid) => setIsPasswordValid(isValid)}
                  className={commonForm("commonElement", { commonInvalid: !(password.length && isPasswordValid) })}
                />
              </CommonFormElement>
              <CommonFormElement
                label={t("Confirm the password")}
                errorMessage={t("Password mismatch")}
                isValid={password === repeatPassword}
              >
                <PasswordInput
                  required
                  className={commonForm("commonElement", { commonInvalid: password !== repeatPassword })}
                  value={repeatPassword}
                  onIonChange={(e: any) => setRepeatPassword(e.detail.value!)}
                />
              </CommonFormElement>

              <IonButton
                color="secondary"
                className="ion-text-capitalize ion-margin-start ion-margin-end"
                expand="block"
                type="submit"
                disabled={!isPasswordValid || password !== repeatPassword}
              >
                {t("Send")}
              </IonButton>
            </form>
          </MaxWidthContainer>
        </div>
        <Page.ContentFooter />
      </Page.Content>
    </Page>
  );
};

export { ResetPassword };
